/**
 * @file BookingForm.page.tsx
 * @path apps/website/src/pages/BookingForm.page.tsx
 * @description Refactored entry point using the unified BookingLayout.
 */

import { SeoHelmet as SEOHelmet } from "@/components";
import { cn } from "@sirbastians/utils";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

// Layout & UI
import { BookingLayout } from "@/components";
import {
  BookingBreadcrumb,
  BookingSidebar,
  BookingStepper,
} from "@/features/booking/components";
import { PriceSummaryBar } from "@/features/booking/components/priceSummaryBar.component";

// Business Logic
import { NAV_LINKS } from "@/config";
import { initStoreFromUrl } from "@/features/booking/booking.store";
import { useBookingWizard } from "@/features/booking/hooks/useBookingWizard.hook";
import { useYocoCheckout } from "@/features/booking/hooks/useYocoCheckout.hook";

// Step Components
import { ActivitiesStep } from "@/features/booking/components/activitiesStep.component";
import { DetailsStep } from "@/features/booking/components/detailsStep.component";
import { ReviewStep } from "@/features/booking/components/reviewStep.component";
import { ScheduleStep } from "@/features/booking/components/scheduleStep.component";
import { ServiceStep } from "@/features/booking/components/serviceStep.component";

export const BookingFormPage = () => {
  const { slug } = useParams<{ slug: string }>();

  // URL pre-fill — run once on mount
  useEffect(() => {
    if (slug) {
      initStoreFromUrl(slug);
    }
  }, [slug]);

  const {
    steps,
    activeStep,
    isFirstStep,
    isLastStep,
    goNext,
    goBack,
    goToStep,
  } = useBookingWizard();

  const { isProcessing } = useYocoCheckout();
  const isReview = activeStep.id === "review";

  // ── Step renderer ─────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (activeStep.id) {
      case "service":
        return <ServiceStep />;
      case "trip-details":
        return <DetailsStep />;
      case "activities":
        return <ActivitiesStep />;
      case "schedule-route":
        return <ScheduleStep />;
      case "review":
        return <ReviewStep onGoToStep={goToStep} />;
      default:
        return <ServiceStep />;
    }
  };

  return (
    <>
      <SEOHelmet
        pageType="default"
        title="Book Your Ride | Sir Bastians"
        description="Secure your transport now. Choose from airport transfers, private hire, or curated tours."
        url={NAV_LINKS.booking.path}
      />

      <BookingLayout
        sidebar={<BookingSidebar onGoToStep={goToStep} />}
        footer={
          !isReview && (
            <PriceSummaryBar
              onNext={goNext}
              onBack={goBack}
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
              nextLabel="Save and Continue"
              isProcessing={isProcessing}
            />
          )
        }
      >
        <div className="mb-8">
          <BookingBreadcrumb />
          <BookingStepper steps={steps} activeStepId={activeStep.id} />
        </div>

        <div
          key={activeStep.id}
          className={cn(
            "animate-in fade-in slide-in-from-bottom-4 duration-300",
          )}
        >
          {renderStep()}
        </div>
      </BookingLayout>
    </>
  );
};
